import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Phone, Mail, MapPin, Clock, Facebook } from "lucide-react";
import logoImage from "@assets/CaribbeanBreeze_logo_over_pictures_1785782527674.webp";

const services = [
  { label: "AC & Heating Installation", href: "/ac-heating-installation" },
  { label: "AC & Heating Repair", href: "/ac-heating-repair" },
  { label: "Ductless Mini-Splits", href: "/ductless-mini-splits" },
  { label: "Commercial HVAC", href: "/commercial-hvac" },
  { label: "Maintenance Plans", href: "/maintenance-plans" },
  { label: "Solar-Powered AC", href: "/solar-powered-ac" },
  { label: "Water Heaters", href: "/contact" },
];

const serviceArea = [
  "Surprise", "Peoria", "Glendale", "Phoenix",
  "Avondale", "Goodyear", "Buckeye", "Litchfield Park", "Tolleson",
];

export default function Footer() {
  return (
    <footer className="bg-card border-t" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 sm:py-10 lg:py-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">

          {/* Brand column */}
          <div className="col-span-2 lg:col-span-1">
            <div className="mb-3 sm:mb-4">
              <img
                src={logoImage}
                alt="Caribbean Breeze HVAC"
                width={78}
                height={64}
                className="h-12 sm:h-16 w-auto object-contain"
              />
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-5 leading-relaxed">
              Family owned and operated since 2017. We deliver honest, dependable
              heating and cooling service across the West Valley.
            </p>
            <div className="space-y-1.5 sm:space-y-2.5 text-xs sm:text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 flex-shrink-0 text-primary" />
                <a
                  href="tel:6027770278"
                  className="hover:text-foreground transition-colors font-medium"
                  data-testid="footer-phone"
                >
                  (602) 777-0278
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 flex-shrink-0 text-primary" />
                <a
                  href="mailto:caribbeanbreezeaz@gmail.com"
                  className="hover:text-foreground transition-colors"
                >
                  caribbeanbreezeaz@gmail.com
                </a>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
                <address className="not-italic" data-testid="text-business-address">
                  Surprise, AZ 85378
                </address>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
                <div>
                  <p>Mon–Fri 8am–5pm</p>
                  <p className="text-xs">Emergency service available</p>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <a
                  href="https://www.facebook.com/share/1Gr6pebiJf/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Caribbean Breeze HVAC on Facebook"
                >
                  <Facebook className="w-4 h-4 text-[#1877F2]" />
                  Facebook
                </a>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-sm mb-2.5 sm:mb-4">Services</h3>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
              {services.map((s) => (
                <li key={s.href}>
                  <Link href={s.href}>
                    <span className="block py-1.5 cursor-pointer hover:text-foreground transition-colors">
                      {s.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-sm mb-2.5 sm:mb-4">Company</h3>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
              <li>
                <Link href="/about">
                  <span className="block py-1.5 cursor-pointer hover:text-foreground transition-colors">About Us</span>
                </Link>
              </li>
              <li>
                <Link href="/financing">
                  <span className="block py-1.5 cursor-pointer hover:text-foreground transition-colors">Financing</span>
                </Link>
              </li>
              <li>
                <Link href="/hvac-resources">
                  <span className="block py-1.5 cursor-pointer hover:text-foreground transition-colors">Blog & Resources</span>
                </Link>
              </li>
              <li>
                <Link href="/contact">
                  <span className="block py-1.5 cursor-pointer hover:text-foreground transition-colors">Contact Us</span>
                </Link>
              </li>
            </ul>

          </div>

          {/* CTA column */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="font-semibold text-sm mb-2.5 sm:mb-4">Get a Free Estimate</h3>
            <p className="hidden sm:block text-sm text-muted-foreground mb-4 leading-relaxed">
              Ready to schedule service or get a quote? Call or fill out the
              contact form — we get back to you fast.
            </p>
            <a href="tel:6027770278" className="block mb-3">
              <Button className="w-full gap-2 font-semibold">
                <Phone className="w-4 h-4" />
                (602) 777-0278
              </Button>
            </a>
            <Link href="/contact">
              <Button variant="outline" className="w-full">
                Schedule Service
              </Button>
            </Link>

            <div className="mt-3 sm:mt-5 grid grid-cols-2 lg:grid-cols-1 gap-2 lg:gap-3">
              <div className="p-2.5 sm:p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <p className="font-semibold text-foreground mb-0.5 sm:mb-1">We Accept</p>
                <p>Cash · Zelle · PayPal · Credit Card</p>
              </div>

              <div className="p-2.5 sm:p-3 rounded-lg bg-primary/5 border border-primary/15 text-xs">
                <p className="font-semibold text-primary mb-0.5">2-Year Labor Warranty</p>
                <p className="text-muted-foreground">On every installation we perform.</p>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-5 sm:my-8" />

        <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg border border-border bg-muted/20 text-xs sm:text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">Service Area: </span>
          {serviceArea.join(" · ")} · and surrounding West Valley communities
        </div>

        <Separator className="mb-4 sm:mb-6" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-2.5 sm:gap-4 text-[11px] sm:text-xs text-muted-foreground text-center md:text-left">
          <p>© {new Date().getFullYear()} Caribbean Breeze HVAC. All rights reserved. Family Owned &amp; Operated Since 2017. Authorized Photon AC Dealer.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="https://www.facebook.com/share/1Gr6pebiJf/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              Facebook
            </a>
            <Link href="/privacy-policy">
              <span className="cursor-pointer hover:text-foreground transition-colors">Privacy</span>
            </Link>
            <Link href="/terms-of-use">
              <span className="cursor-pointer hover:text-foreground transition-colors">Terms</span>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
